import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Ic, type IconComponent } from './Ic';

/** Centered dialog. Enter is CSS-only (@starting-style in components.css).
 *  Exit: pass `open={false}` and keep rendering (see usePresence) — the modal
 *  plays its exit transition, then calls `onExited`. Without `open`/`onExited`
 *  it behaves as before: mounted means open. */
export function Modal({
  title,
  icon: Icon,
  onClose,
  children,
  footer,
  size,
  open = true,
  onExited,
}: {
  title: string;
  icon?: IconComponent;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'lg';
  open?: boolean;
  onExited?: () => void;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const scrimRef = useRef<HTMLDivElement>(null);
  // Callers pass inline arrows; a ref keeps the effect below from re-running
  // (and re-stealing focus from inputs inside the dialog) on every render.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const returnTo = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Keyboard actions never animate: close instantly.
      scrimRef.current?.setAttribute('data-instant', 'true');
      onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    // Move focus in so Escape works and screen readers land inside the dialog.
    panelRef.current?.focus({ preventScroll: true });
    return () => {
      document.removeEventListener('keydown', onKey);
      // Hand focus back to whatever opened the dialog.
      if (returnTo?.isConnected && returnTo !== document.body) returnTo.focus({ preventScroll: true });
    };
  }, [open]);

  useEffect(() => {
    const scrim = scrimRef.current;
    const panel = panelRef.current;
    panel?.toggleAttribute('inert', !open);
    if (open) {
      scrim?.removeAttribute('data-instant');
      return;
    }
    if (!scrim) {
      onExited?.();
      return;
    }
    // Unmount once the exit transitions settle. Duration-agnostic: with
    // reduced motion or data-instant there are no transitions and this
    // resolves immediately. The timeout covers a transition that never
    // finishes (e.g. hidden tab).
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      onExited?.();
    };
    const safety = setTimeout(done, 400);
    const frame = requestAnimationFrame(() => {
      void Promise.allSettled(scrim.getAnimations({ subtree: true }).map((a) => a.finished)).then(done);
    });
    return () => {
      settled = true;
      clearTimeout(safety);
      cancelAnimationFrame(frame);
    };
  }, [open, onExited]);

  // Portalled so no transformed or overflow-clipped ancestor can capture the
  // fixed-position scrim.
  return createPortal(
    <div
      className="modal-scrim"
      ref={scrimRef}
      data-state={open ? 'open' : 'closed'}
      // mousedown, not click: a drag that starts inside the panel and releases
      // on the scrim would otherwise close the modal mid-selection.
      onMouseDown={(e) => {
        if (open && e.target === scrimRef.current) onClose();
      }}
    >
      <div
        className={`modal ${size === 'lg' ? 'lg' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={panelRef}
      >
        <div className="modal-head">
          {Icon && <Icon className="ic" />}
          <h3>{title}</h3>
          <button className="btn ghost sm icon x" onClick={onClose} aria-label="Close">
            <Ic.x className="ico" />
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
